import React, { useEffect, useState } from "react";
import { Avatar, Form, Select } from "antd";
import { SpellCheckTextArea } from "../spellcheck";
import { Input, message, Button } from "antd";
import ImageUploadButton from "../inputs/ImageUploadButton";
import gallery from "../../resources/gallery.svg";
import { cloudFrontUrl, serverUrl } from "../../nestserver";
import { useQueryClient } from "react-query";
import axios from "axios";
import { useSelector } from "react-redux";
import { selectUser } from "../../redux/slices/auth.slice";

function Announcement() {
  const user = useSelector(selectUser);
  const queryClient = useQueryClient();
  const [form] = Form.useForm();

  const [media, setMedia] = useState([]);
  // Branches of the caller's school group — empty for everyone who is not a
  // group admin, which is how we decide whether to show the "post to" picker
  // at all. Fetched here rather than shared with Header.js: this is the only
  // other place in the app that needs the list, and duplicating one GET is
  // simpler than threading a shared cache through for a single consumer.
  const [groupSchools, setGroupSchools] = useState([]);

  useEffect(() => {
    if (!user?.group) return;
    axios
      .get(serverUrl + "/auth/my-schools", {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      })
      .then((res) => {
        if (Array.isArray(res.data?.result)) setGroupSchools(res.data.result);
      })
      .catch((err) => console.log("Error loading group schools", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.group]);

  const onAnnounce = (values) => {
    const imageData = media
      .filter((item) => item.type === "IMAGE")
      .map((img, index) => ({
        id: `img_${Date.now()}_${index}`,
        name: img.name || `image_${index}.jpg`,
        url: img.url || img.file,
      }));

    const mediaData = media.map((item, index) => ({
      id: item.id || `media_${Date.now()}_${index}`,
      name: item.name || `media_${index}`,
      type: item.type,
      url: item.url || item.file,
    }));

    values["images"] = imageData;
    values["media"] = mediaData;
    values["user"] = user?._id;

    axios({
      method: "post",
      url: serverUrl + "/announcement",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
      data: { ...values, content: values?.content?.trim() },
    })
      .then((res) => {
        message.success("Announcement Posted Successfully");
        form.resetFields();
        queryClient.invalidateQueries("announcement");
        setMedia([]);
      })
      .catch((e) => {
        message.error(e.message);
      });
  };

  const handleMediaUpload = (uploadedFileId, file) => {
    if (media.length >= 10) {
      message.warning("You can only upload up to 10 media files");
      return;
    }

    let mediaType = "IMAGE";
    if (file.type.startsWith("video/")) {
      mediaType = "VIDEO";
    } else if (file.type.startsWith("audio/")) {
      mediaType = "AUDIO";
    }

    setMedia((prev) => [
      ...prev,
      {
        id: uploadedFileId,
        file: uploadedFileId,
        name: file.name || `media_${Date.now()}`,
        url: uploadedFileId,
        type: mediaType,
        mimeType: file.type,
      },
    ]);
  };

  const removeMedia = (indexToRemove) => {
    setMedia((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const getImageDisplayUrl = (image) => {
    if (!image) return "";
    if (typeof image === "string") {
      return cloudFrontUrl + "/" + user?._id + "/" + image;
    }
    if (image.url) {
      return cloudFrontUrl + "/" + user?._id + "/" + image.url;
    }
    if (image.image) {
      return cloudFrontUrl + "/" + user?._id + "/" + image.image;
    }
    return "";
  };

  return (
    <div className="pt-8">
      <div className="bg-white drop-shadow-md rounded-lg">
        <Form name="basic" initialValues={{}} onFinish={onAnnounce} form={form}>
          <div className="p-5">
            <div
              style={{ color: "#616161" }}
              className="flex flex-row w-full space-x-3 text-left text-lg"
            >
              <div>
                <Avatar
                  size={50}
                  src={
                    user[user?.role === "instructor" ? "teacher" : user?.role]
                      ?.profile_image
                      ? cloudFrontUrl +
                        "/" +
                        user?._id +
                        "/" +
                        user[
                          user?.role === "instructor" ? "teacher" : user?.role
                        ]?.profile_image
                      : "https://ui-avatars.com/api/?name=" +
                        user[
                          user?.role === "instructor" ? "teacher" : user?.role
                        ]?.first_name +
                        " " +
                        user[
                          user?.role === "instructor" ? "teacher" : user?.role
                        ]?.last_name
                  }
                  alt="logo"
                ></Avatar>
              </div>

              <div className="w-full px-1">
                <Form.Item
                  name="content"
                  rules={[
                    {
                      required: true,
                      message: "Please input the content",
                    },
                  ]}
                >
                  <SpellCheckTextArea
                    rows={6}
                    placeholder="What's on your mind?"
                    className="w-full bg-gray-300 focus:outline-none resize-none"
                  />
                </Form.Item>
              </div>
            </div>

            {/* Selected Media Preview */}
            {media.length > 0 && (
              <div className="pl-16 pb-3">
                <div className="text-sm text-gray-500 mb-2">
                  Attached Media ({media.length}/10)
                </div>
                <div className="flex flex-wrap gap-3">
                  {media.map((item, index) => (
                    <div key={index} className="relative w-20 h-20">
                      <div className="border-2 border-gray-200 rounded-lg bg-gray-100 shadow-sm w-full h-full flex items-center justify-center overflow-hidden hover:border-blue-400 transition-colors">
                        {item.type === "IMAGE" && (
                          <img
                            src={getImageDisplayUrl(item)}
                            alt={`Upload ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        )}
                        {item.type === "VIDEO" && (
                          <div className="relative w-full h-full bg-gray-900">
                            <video
                              src={getImageDisplayUrl(item)}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                              <div className="text-white text-3xl">▶️</div>
                            </div>
                          </div>
                        )}
                        {item.type === "AUDIO" && (
                          <div className="relative w-full h-full bg-gradient-to-br from-pink-100 to-orange-100 flex items-center justify-center">
                            <div className="text-3xl">🎵</div>
                          </div>
                        )}
                      </div>
                      {/* Remove button */}
                      <button
                        type="button"
                        onClick={() => removeMedia(index)}
                        className="absolute top-0 right-0 rounded-full flex items-center justify-center font-bold shadow-lg hover:scale-110 transition-transform z-10"
                        style={{
                          width: "24px",
                          height: "24px",
                          backgroundColor: "#ef4444",
                          color: "white",
                          fontSize: "16px",
                          cursor: "pointer",
                        }}
                        title="Remove media"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between pt-3 pl-16">
              <div className="flex items-center space-x-2">
                <ImageUploadButton
                  folder={user?._id}
                  ratio={1}
                  onUpload={handleMediaUpload}
                  multiple={true}
                  disabled={media.length >= 10}
                  allowedTypes={["image", "video", "audio"]}
                  maxSize={50}
                >
                  <div
                    style={{
                      borderColor: "#00627B",
                      opacity: media.length >= 10 ? 0.5 : 1,
                    }}
                    className="py-2 px-4 rounded border"
                  >
                    <div className="flex space-x-1">
                      <img src={gallery} alt="Upload media" />
                      <div>
                        Upload media{" "}
                        {media.length > 0 && `(${media.length}/10)`}
                      </div>
                    </div>
                  </div>
                </ImageUploadButton>
              </div>
              <div className="flex items-center h-full space-x-2">
                {groupSchools.length > 1 && (
                  <Form.Item name="targetSchool" initialValue="all" className="mb-0">
                    <Select
                      style={{ width: 200 }}
                      options={[
                        { value: "all", label: "All Branches" },
                        ...groupSchools.map((s) => ({
                          value: s._id,
                          label: s.name,
                        })),
                      ]}
                    />
                  </Form.Item>
                )}
                <Form.Item className="mb-0">
                  <Button size="large" type="primary" htmlType="submit">
                    Post
                  </Button>
                </Form.Item>
              </div>
            </div>
          </div>
        </Form>
      </div>
    </div>
  );
}

export default Announcement;
